/**
 * Leistungskatalog und Angebotsvorlagen.
 *
 * Zwei Listen, die zusammengehören: der Katalog trägt die Preise, die
 * Vorlagen setzen sie zu ganzen Angeboten zusammen.
 *
 * Der wichtigste Unterschied steht auch auf der Seite, weil er sonst
 * überrascht: eine **Vorlage folgt** dem Katalogpreis, ein geschriebener
 * **Beleg nicht**. Ein Beleg zeigt, was zum Zeitpunkt des Schreibens galt
 * – das ist der Grund, warum eine Preisänderung hier keine einzige alte
 * Rechnung berührt.
 *
 * Die Prüfart an einer Leistung ist mehr als eine Beschriftung: sie sagt
 * dem Sammeln, welcher Preis für eine fertige Prüfung gilt. Deshalb darf
 * es je Prüfart nur eine aktive Leistung geben, und deshalb weist der
 * Server eine zweite ab.
 */

import { useState } from 'react'
import {
  useLeistungAendern,
  useLeistungAnlegen,
  useLeistungLoeschen,
  useLeistungen,
  useVorlageAendern,
  useVorlageAnlegen,
  useVorlageLoeschen,
  useVorlagen,
  useVorlagenposition,
  type Leistung,
  type Vorlage,
} from '../api/abrechnung'
import { euro } from '../api/belege'
import { PRUEFARTTEXT, type Pruefart } from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis } from '../bausteine/Hinweise'

const ARTEN: Pruefart[] = ['hauptuntersuchung', 'operativ', 'abnahme', 'nachpruefung']

export function Leistungskatalog() {
  const [teil, setzeTeil] = useState<'katalog' | 'vorlagen'>('katalog')

  return (
    <div className="space-y-4">
      <div className="flex gap-2" role="tablist" aria-label="Bereich">
        {(
          [
            ['katalog', 'Leistungen'],
            ['vorlagen', 'Angebotsvorlagen'],
          ] as const
        ).map(([wert, text]) => (
          <button
            key={wert}
            type="button"
            role="tab"
            aria-selected={teil === wert}
            className={teil === wert ? 'knopf-haupt flex-1' : 'knopf-still flex-1'}
            onClick={() => setzeTeil(wert)}
          >
            {text}
          </button>
        ))}
      </div>

      {teil === 'katalog' ? <Katalog /> : <Vorlagen />}
    </div>
  )
}

// ── Leistungskatalog ────────────────────────────────────────────────

function Katalog() {
  const liste = useLeistungen({ nurAktive: false })
  const [bearbeitet, setzeBearbeitet] = useState<Leistung | 'neu' | null>(null)

  if (liste.isLoading) return <Ladeanzeige />
  if (liste.error) return <Fehlerhinweis fehler={liste.error} />

  const leistungen = liste.data ?? []
  const ohnePreis = ARTEN.filter(
    (a) => !leistungen.some((l) => l.aktiv && l.pruefart === a),
  )

  return (
    <div className="space-y-4">
      <p className="rounded-feld border-2 border-marke-100 bg-marke-50 p-3 text-marke-700">
        Preise für die Abrechnung. Eine Leistung mit Prüfart wird beim Sammeln
        fertiger Prüfungen von selbst angesetzt; eine ohne (Anfahrt, Beratung,
        Gutachten) steht nur zur Auswahl, wenn man ein Angebot schreibt.
      </p>

      {ohnePreis.length > 0 && leistungen.length > 0 && (
        <p className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3 text-stufe-2">
          Ohne Preis:{' '}
          {ohnePreis.map((a) => PRUEFARTTEXT[a]).join(', ')}. Prüfungen dieser Art
          landen beim Sammeln mit 0,00 € auf der Rechnung.
        </p>
      )}

      <button
        type="button"
        className="knopf-haupt w-full"
        onClick={() => setzeBearbeitet('neu')}
      >
        Leistung hinzufügen
      </button>

      {leistungen.length === 0 ? (
        <Leerhinweis
          titel="Noch keine Leistungen"
          text="Ohne Katalog entsteht beim Sammeln je Prüfung eine Zeile mit 0,00 €. Ein Eintrag je Prüfart genügt für den Anfang."
        />
      ) : (
        <ul className="space-y-2">
          {leistungen.map((l) => (
            <li key={l.id}>
              <button
                type="button"
                className="karte karte-knopf w-full text-left"
                onClick={() => setzeBearbeitet(l)}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">{l.bezeichnung}</span>
                  <span className="text-lg font-bold tabular-nums">
                    {euro(l.einzelpreis)}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-base text-slate-600">
                  <span>je {l.einheit}</span>
                  {l.pruefart && (
                    <span className="marke bg-akzent-grund text-akzent-700">
                      {PRUEFARTTEXT[l.pruefart]}
                    </span>
                  )}
                  {!l.aktiv && (
                    <span className="marke bg-slate-100 text-slate-600">stillgelegt</span>
                  )}
                  {l.verwendungen > 0 && <span>{l.verwendungen}× eingesetzt</span>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {bearbeitet && (
        <Leistungsblatt
          leistung={bearbeitet === 'neu' ? null : bearbeitet}
          schliessen={() => setzeBearbeitet(null)}
        />
      )}
    </div>
  )
}

function Leistungsblatt({
  leistung,
  schliessen,
}: {
  leistung: Leistung | null
  schliessen: () => void
}) {
  const anlegen = useLeistungAnlegen()
  const aendern = useLeistungAendern()
  const loeschen = useLeistungLoeschen()
  const [sicherheitsfrage, setzeSicherheitsfrage] = useState(false)

  const laeuft = anlegen.isPending || aendern.isPending || loeschen.isPending

  return (
    <Blatt
      titel={leistung ? leistung.bezeichnung : 'Neue Leistung'}
      untertitel={leistung ? 'Katalogeintrag bearbeiten' : 'Preis für die Abrechnung'}
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="leistung-form"
            className="knopf-haupt flex-1"
            disabled={laeuft}
          >
            {laeuft ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      }
    >
      <form
        id="leistung-form"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const f = new FormData(e.currentTarget)
          const t = (n: string) => String(f.get(n) ?? '').trim()
          const daten = {
            bezeichnung: t('bezeichnung'),
            beschreibung: t('beschreibung') || null,
            einheit: t('einheit') || 'Stück',
            einzelpreis: t('einzelpreis') || '0',
            steuersatz: t('steuersatz') || '19',
            pruefart: (t('pruefart') || null) as Pruefart | null,
            aktiv: f.get('aktiv') === 'on',
          }
          const auftrag = leistung
            ? aendern.mutateAsync({ id: leistung.id, ...daten })
            : anlegen.mutateAsync(daten)
          void auftrag.then(schliessen).catch(() => {})
        }}
      >
        <div>
          <label htmlFor="lk-bez">Bezeichnung</label>
          <input
            id="lk-bez"
            name="bezeichnung"
            required
            minLength={2}
            maxLength={200}
            defaultValue={leistung?.bezeichnung ?? ''}
            placeholder="Jährliche Hauptuntersuchung"
          />
        </div>

        <div>
          <label htmlFor="lk-beschr">Beschreibung</label>
          <textarea
            id="lk-beschr"
            name="beschreibung"
            rows={2}
            defaultValue={leistung?.beschreibung ?? ''}
            placeholder="Steht auf der Rechnung unter der Bezeichnung."
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="lk-preis">Einzelpreis netto</label>
            <input
              id="lk-preis"
              name="einzelpreis"
              type="number"
              step="0.01"
              required
              defaultValue={leistung?.einzelpreis ?? ''}
            />
          </div>
          <div>
            <label htmlFor="lk-einheit">Einheit</label>
            <input
              id="lk-einheit"
              name="einheit"
              maxLength={20}
              defaultValue={leistung?.einheit ?? 'Stück'}
            />
          </div>
          <div>
            <label htmlFor="lk-steuer">Steuersatz %</label>
            <input
              id="lk-steuer"
              name="steuersatz"
              type="number"
              step="0.01"
              defaultValue={leistung?.steuersatz ?? '19.00'}
            />
          </div>
        </div>

        <div>
          <label htmlFor="lk-art">Rechnet diese Prüfart ab</label>
          <select id="lk-art" name="pruefart" defaultValue={leistung?.pruefart ?? ''}>
            <option value="">— keine, nur zur Auswahl —</option>
            {ARTEN.map((a) => (
              <option key={a} value={a}>
                {PRUEFARTTEXT[a]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-base text-slate-600">
            Gesetzt heißt: beim Sammeln fertiger Prüfungen dieser Art wird dieser
            Preis angesetzt. Je Prüfart geht nur eine aktive Leistung.
          </p>
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            name="aktiv"
            className="mt-1 h-6 w-6"
            defaultChecked={leistung?.aktiv ?? true}
          />
          <span>
            Aktiv
            <span className="mt-0.5 block font-normal text-slate-600">
              Stillgelegte Leistungen stehen nicht mehr zur Auswahl und werden
              nicht mehr angesetzt. Geschriebene Belege bleiben unberührt.
            </span>
          </span>
        </label>

        <Fehlerhinweis fehler={anlegen.error ?? aendern.error ?? loeschen.error} />

        {leistung && (
          <div className="border-t-2 border-slate-200 pt-3">
            {sicherheitsfrage ? (
              <div className="space-y-2">
                <p>
                  Endgültig löschen? Geschriebene Belege bleiben, wie sie sind –
                  dort steht der Preis als Abschrift. Vorlagen verlieren die
                  Zeile.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="knopf-still flex-1"
                    onClick={() => setzeSicherheitsfrage(false)}
                  >
                    Behalten
                  </button>
                  <button
                    type="button"
                    className="knopf-warnung flex-1"
                    disabled={laeuft}
                    onClick={() =>
                      void loeschen
                        .mutateAsync(leistung.id)
                        .then(schliessen)
                        .catch(() => {})
                    }
                  >
                    Löschen
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="knopf-warnung w-full"
                onClick={() => setzeSicherheitsfrage(true)}
              >
                Leistung löschen
              </button>
            )}
          </div>
        )}
      </form>
    </Blatt>
  )
}

// ── Angebotsvorlagen ────────────────────────────────────────────────

function Vorlagen() {
  const liste = useVorlagen()
  const anlegen = useVorlageAnlegen()
  const [offen, setzeOffen] = useState<Vorlage | null>(null)
  const [neu, setzeNeu] = useState(false)

  if (liste.isLoading) return <Ladeanzeige />
  if (liste.error) return <Fehlerhinweis fehler={liste.error} />

  const vorlagen = liste.data ?? []
  // Nach jeder Änderung frisch aus der Liste holen: sonst zeigt das offene
  // Blatt noch den Stand von vor dem Hinzufügen einer Position.
  const gezeigt = offen ? (vorlagen.find((v) => v.id === offen.id) ?? null) : null

  return (
    <div className="space-y-4">
      <p className="rounded-feld border-2 border-marke-100 bg-marke-50 p-3 text-marke-700">
        Ganze Angebote als Muster: Betreff, Anschreiben und ein Satz Positionen.
        Eine Vorlage <span className="font-semibold">folgt</span> dem
        Katalogpreis — anders als ein geschriebener Beleg, der zeigt, was
        damals galt.
      </p>

      <button
        type="button"
        className="knopf-haupt w-full"
        onClick={() => setzeNeu(true)}
      >
        Vorlage hinzufügen
      </button>

      {vorlagen.length === 0 ? (
        <Leerhinweis
          titel="Noch keine Vorlagen"
          text="Eine Vorlage lohnt sich für den Fall, den du oft schreibst – etwa das jährliche Angebot an eine Gemeinde."
        />
      ) : (
        <ul className="space-y-2">
          {vorlagen.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                className="karte karte-knopf w-full text-left"
                onClick={() => setzeOffen(v)}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold">{v.name}</span>
                  <span className="text-lg font-bold tabular-nums">
                    {euro(v.netto)}
                  </span>
                </div>
                <p className="mt-1 text-base text-slate-600">
                  {v.positionen.length}{' '}
                  {v.positionen.length === 1 ? 'Position' : 'Positionen'}
                  {v.betreff ? ` · ${v.betreff}` : ''}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {neu && (
        <Blatt
          titel="Neue Vorlage"
          schliessen={() => setzeNeu(false)}
          fuss={
            <div className="flex gap-3">
              <button
                type="button"
                className="knopf-still flex-1"
                onClick={() => setzeNeu(false)}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                form="vorlage-neu"
                className="knopf-haupt flex-1"
                disabled={anlegen.isPending}
              >
                {anlegen.isPending ? 'Legt an …' : 'Anlegen'}
              </button>
            </div>
          }
        >
          <form
            id="vorlage-neu"
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              const f = new FormData(e.currentTarget)
              const t = (n: string) => String(f.get(n) ?? '').trim()
              void anlegen
                .mutateAsync({
                  name: t('name'),
                  betreff: t('betreff') || null,
                  einleitung: t('einleitung') || null,
                  schluss: t('schluss') || null,
                })
                .then((v) => {
                  setzeNeu(false)
                  setzeOffen(v)
                })
                .catch(() => {})
            }}
          >
            <div>
              <label htmlFor="vn-name">Name der Vorlage</label>
              <input
                id="vn-name"
                name="name"
                required
                minLength={2}
                maxLength={120}
                placeholder="Jahresangebot Gemeinde"
              />
              <p className="mt-1 text-base text-slate-600">
                Nur für die Auswahlliste. Der Kunde sieht ihn nicht.
              </p>
            </div>
            <div>
              <label htmlFor="vn-betreff">Betreff</label>
              <input id="vn-betreff" name="betreff" maxLength={200} />
            </div>
            <div>
              <label htmlFor="vn-einl">Anschreiben</label>
              <textarea id="vn-einl" name="einleitung" rows={3} />
            </div>
            <div>
              <label htmlFor="vn-schluss">Schlusstext</label>
              <textarea id="vn-schluss" name="schluss" rows={2} />
            </div>
            <Fehlerhinweis fehler={anlegen.error} />
          </form>
        </Blatt>
      )}

      {gezeigt && (
        <Vorlagenblatt vorlage={gezeigt} schliessen={() => setzeOffen(null)} />
      )}
    </div>
  )
}

function Vorlagenblatt({
  vorlage,
  schliessen,
}: {
  vorlage: Vorlage
  schliessen: () => void
}) {
  const leistungen = useLeistungen()
  const aendern = useVorlageAendern()
  const loeschen = useVorlageLoeschen()
  const position = useVorlagenposition()
  const [sicherheitsfrage, setzeSicherheitsfrage] = useState(false)

  const auswahl = (leistungen.data ?? []).filter(
    (l) => !vorlage.positionen.some((p) => p.leistung_id === l.id),
  )

  return (
    <Blatt
      titel={vorlage.name}
      untertitel={`${vorlage.positionen.length} Positionen · ${euro(vorlage.netto)} netto`}
      schliessen={schliessen}
      breit
      fuss={
        <button type="button" className="knopf-haupt w-full" onClick={schliessen}>
          Fertig
        </button>
      }
    >
      <div className="space-y-4">
        <section className="space-y-2">
          <h3 className="font-bold">Positionen</h3>
          {vorlage.positionen.length === 0 ? (
            <p className="text-slate-600">Noch keine. Unten eine Leistung wählen.</p>
          ) : (
            <ul className="space-y-2">
              {vorlage.positionen.map((p) => (
                <li key={p.id} className="karte flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{p.bezeichnung}</div>
                    <div className="text-base text-slate-600">
                      {euro(p.einzelpreis)} je {p.einheit}
                    </div>
                  </div>
                  <label className="shrink-0">
                    <span className="nur-vorlesen">Menge für {p.bezeichnung}</span>
                    <input
                      type="number"
                      step="0.01"
                      className="w-24"
                      defaultValue={p.menge}
                      onBlur={(e) => {
                        const wert = e.target.value.trim()
                        if (!wert || wert === p.menge) return
                        position.aendern.mutate({
                          vorlageId: vorlage.id,
                          id: p.id,
                          menge: wert,
                        })
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="knopf-warnung h-12 w-12 shrink-0 px-0"
                    aria-label={`${p.bezeichnung} entfernen`}
                    onClick={() =>
                      position.loeschen.mutate({ vorlageId: vorlage.id, id: p.id })
                    }
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          {auswahl.length > 0 && (
            <div>
              <label htmlFor="vb-neu">Leistung hinzufügen</label>
              <select
                id="vb-neu"
                value=""
                onChange={(e) => {
                  if (!e.target.value) return
                  position.anlegen.mutate({
                    vorlageId: vorlage.id,
                    leistung_id: e.target.value,
                  })
                }}
              >
                <option value="">Bitte wählen</option>
                {auswahl.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.bezeichnung} — {euro(l.einzelpreis)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Fehlerhinweis
            fehler={
              position.anlegen.error ??
              position.aendern.error ??
              position.loeschen.error
            }
          />
        </section>

        <section className="space-y-3 border-t-2 border-slate-200 pt-3">
          <h3 className="font-bold">Texte</h3>
          <div>
            <label htmlFor="vb-betreff">Betreff</label>
            <input
              id="vb-betreff"
              maxLength={200}
              defaultValue={vorlage.betreff ?? ''}
              onBlur={(e) =>
                e.target.value !== (vorlage.betreff ?? '') &&
                aendern.mutate({ id: vorlage.id, betreff: e.target.value || null })
              }
            />
          </div>
          <div>
            <label htmlFor="vb-einl">Anschreiben</label>
            <textarea
              id="vb-einl"
              rows={3}
              defaultValue={vorlage.einleitung ?? ''}
              onBlur={(e) =>
                e.target.value !== (vorlage.einleitung ?? '') &&
                aendern.mutate({ id: vorlage.id, einleitung: e.target.value || null })
              }
            />
          </div>
          <div>
            <label htmlFor="vb-schluss">Schlusstext</label>
            <textarea
              id="vb-schluss"
              rows={2}
              defaultValue={vorlage.schluss ?? ''}
              onBlur={(e) =>
                e.target.value !== (vorlage.schluss ?? '') &&
                aendern.mutate({ id: vorlage.id, schluss: e.target.value || null })
              }
            />
          </div>
          <p className="text-base text-slate-600">
            Änderungen werden beim Verlassen des Feldes gespeichert.
          </p>
        </section>

        <div className="border-t-2 border-slate-200 pt-3">
          {sicherheitsfrage ? (
            <div className="space-y-2">
              <p>Vorlage endgültig löschen?</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="knopf-still flex-1"
                  onClick={() => setzeSicherheitsfrage(false)}
                >
                  Behalten
                </button>
                <button
                  type="button"
                  className="knopf-warnung flex-1"
                  onClick={() =>
                    void loeschen
                      .mutateAsync(vorlage.id)
                      .then(schliessen)
                      .catch(() => {})
                  }
                >
                  Löschen
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="knopf-warnung w-full"
              onClick={() => setzeSicherheitsfrage(true)}
            >
              Vorlage löschen
            </button>
          )}
        </div>
      </div>
    </Blatt>
  )
}
